import React, {createContext, useContext, useState, useEffect, useCallback} from 'react';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {useLocation} from 'react-router-dom';
import {type IExperimentData} from '@ezcater/experiments-js/dist/types';
import type {ExperimentsClientWithExtract} from '../graphql/createExperimentsClient';
import getExperimentsForPath from '../util/getExperimentsForPath';

interface LoadExperimentsProps {
  trackingId?: string;
  experiments?: string[];
}

interface ExperimentsContextType {
  experiments: IExperimentData;
  isLoading: boolean;
  loadExperiments: (props?: LoadExperimentsProps) => Promise<IExperimentData>;
}

const ExperimentsContext = createContext<ExperimentsContextType>({
  experiments: {},
  isLoading: true,
  loadExperiments: async () => ({}),
});

interface ExperimentsProviderProps {
  children: React.ReactNode;
  experimentsClient: ExperimentsClientWithExtract;
  apolloClient: ApolloClient<NormalizedCacheObject>;
}

export const ExperimentsProvider: React.FC<ExperimentsProviderProps> = ({
  children,
  experimentsClient,
  apolloClient,
}) => {
  const location = useLocation();
  const [experiments, setExperiments] = useState<IExperimentData>(
    experimentsClient.extract() || {}
  );
  const [isLoading, setIsLoading] = useState(false);

  const loadExperiments = useCallback(
    async (props?: LoadExperimentsProps) => {
      if (!experimentsClient) {
        return {};
      }
      setIsLoading(true);
      try {
        const result = await experimentsClient.loadExperiments(props);
        setExperiments({...result, ...experimentsClient.extract()});
        return result;
      } finally {
        setIsLoading(false);
      }
    },
    [experimentsClient]
  );

  // if a page is loaded by SPA navigation, we need to load experiments
  useEffect(() => {
    const loadInitialExperiments = async () => {
      setIsLoading(true);
      if (Object.keys(experiments).length === 0 && typeof window !== 'undefined') {
        const {
          experiments: pathExperiments,
          shouldLoad,
          trackingId,
        } = await getExperimentsForPath(location.pathname, apolloClient);
        if (shouldLoad) {
          await loadExperiments({experiments: pathExperiments, trackingId});
        }
      }
      setIsLoading(false);
    };

    loadInitialExperiments();
  }, [apolloClient, experiments, loadExperiments, location.pathname]);

  return (
    <ExperimentsContext.Provider value={{experiments, isLoading, loadExperiments}}>
      {children}
    </ExperimentsContext.Provider>
  );
};

export const useExperiments = () => {
  const context = useContext(ExperimentsContext);
  if (!context) {
    throw new Error('useExperiments must be used within an ExperimentsProvider');
  }
  return context;
};

export const useExperiment = (experimentName: string) => {
  const {experiments} = useExperiments();
  return experiments[experimentName]?.variant;
};

export default ExperimentsProvider;
